import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [stage, setStage] = useState('checking'); // checking | ready | done | error
  const [maskedEmail, setMaskedEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setErrorMsg('No reset token provided.');
        setStage('error');
        return;
      }
      try {
        const res = await authAPI.passwordResetCheck(token);
        if (cancelled) return;
        setMaskedEmail(res.data.email || '');
        setStage('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err?.response?.data?.detail || 'This reset link is invalid or has expired.');
        setStage('error');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.passwordResetConsume(token, pw);
      setStage('done');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reset password');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#060913] text-white flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <img src="/logo-main.png" alt="Elev802" className="h-20 w-auto mx-auto rounded-2xl" />
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mt-4">RESET PASSWORD</h1>
        </div>

        <div className="bg-[#0F172A] border border-[#1C2B54] rounded-2xl p-6">
          {stage === 'checking' && (
            <div className="flex flex-col items-center py-8 text-[#94A3B8]" data-testid="reset-checking">
              <Loader2 size={24} className="animate-spin mb-2" />
              <p className="text-sm">Verifying link…</p>
            </div>
          )}

          {stage === 'error' && (
            <div className="text-center py-4" data-testid="reset-error">
              <AlertTriangle size={28} className="mx-auto text-[#EF4444] mb-3" />
              <p className="text-sm text-[#CBD5E1] mb-4">{errorMsg}</p>
              <button
                onClick={() => navigate('/login')}
                className="text-[#00E5FF] text-xs font-bold uppercase tracking-widest underline-offset-2 hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          )}

          {stage === 'ready' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
              {maskedEmail && (
                <p className="text-xs text-[#94A3B8] text-center">
                  Setting a new password for <span className="text-white font-semibold">{maskedEmail}</span>
                </p>
              )}

              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">New password</span>
                <div className="relative mt-1">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full bg-[#060913] border border-[#1C2B54] rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                    autoFocus
                    required
                    data-testid="reset-pw-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>

              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Confirm new password</span>
                <div className="relative mt-1">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="Re-type the new password"
                    className="w-full bg-[#060913] border border-[#1C2B54] rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                    required
                    data-testid="reset-pw2-input"
                  />
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-6 py-3 hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Update Password'}
              </button>
            </form>
          )}

          {stage === 'done' && (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 size={32} className="mx-auto text-[#10B981] mb-3" />
              <p className="font-['Barlow_Condensed'] text-lg font-bold mb-1">Password updated</p>
              <p className="text-xs text-[#94A3B8] mb-4">You can now sign in with your new password.</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-white text-[#060913] font-black uppercase tracking-wide rounded-lg px-6 py-3 hover:opacity-90 transition-opacity"
              >
                Go to Sign In
              </button>
            </div>
          )}
        </div>

        <p className="text-[10px] text-[#64748B] text-center mt-4 tracking-wide">
          Reset links expire 1 hour after they're issued.
        </p>
      </motion.div>
    </div>
  );
}
